import { css } from "@emotion/react"
import { useMemo, useState, useCallback } from "react"
import { useIntl } from "react-intl"
import { ScoreIndicator, backendColors, colors, fonts, getSortedTrainingGoals } from "shared-ui"
import Modal from "../_layout/Modal"
import IndexGauge from "./IndexGauge"
import PitchTypeIcon from "../pitch/PitchTypeIcon"
import dp from 'dompurify'
import { lighten } from "polished"

export default function IndexResultsModal({ pitch, indexData, referenceTimestamp, showExpired = true, ...props }) {

    const { formatMessage } = useIntl()

    const trainingGoals = useMemo(() => {
        return getSortedTrainingGoals(indexData, new Date(referenceTimestamp))
            .map(ind => ({
                id: ind.indexCategory.id,
                color: backendColors[ind.score.assessment.frontendCode],
                value: Math.round(ind.value * 10) / 10,
                name: ind.indexCategory.name,
                description: ind.indexCategory.description,
                expired: ind._isExpired_
            }))
    }, [indexData])

    const [selectedItem, setSelectedItem] = useState()

    const handleClickItem = useCallback((id) => {
        setSelectedItem(curr => curr === id ? undefined : id)
    }, [])

    return <Modal
        header={
            <div
                title={pitch?.type}
                css={css`
                    display: flex; 
                    align-items: 
                    center; gap: 1em;
                    
                    @media screen and (max-width: 600px) {
                        font-size: 0.75em;
                    }
                `}
            >
                <PitchTypeIcon
                    pitch={pitch}
                    css={css`
                        height: 2em;
                        width: 2em;
                        @media screen and (max-width: 600px) {
                            display: none;
                        }
                    `}
                />
                <h2 css={style.title}>
                    {pitch?.name}
                </h2>
            </div>
        }
        children={
            <>
                <h3 css={[css`margin-bottom: 0.875em;`, appearance.h3]}>
                    {formatMessage({ id: 'rawPerformanceIndex' })}
                </h3>
                <div css={style.gauges}>
                    {indexData
                        .filter(ind => ind.indexCategory.indexCategoryGroup === 'performance')
                        .toSorted((a, b) => a.indexCategory.sortRank < b.indexCategory.sortRank ? -1 : 1)
                        .map((ind, i) => {
                            return <div
                                key={`indG-${i}`}
                                css={ind._isExpired_ ? appearance.isExpired(showExpired) : null}
                            >
                                <IndexGauge
                                    size={80}
                                    indexData={{
                                        localisedName: ind.indexCategory?.name,
                                        indexValue: ind.value,
                                        frontendCode: ind.score?.assessment?.frontendCode,
                                        indexType: ind.indexCategory.indexCategoryGroup,
                                    }}
                                />
                            </div>
                        })}
                </div>

                {(trainingGoals.length > 0) &&

                    <>
                        <h3 css={[
                            css`
                                margin-bottom: 0.875em; 
                                display: flex; 
                                justify-content: space-between; 
                                margin-right: 0.5em;
                                gap: 1em;
                            `,
                            appearance.h3
                        ]}>
                            <span>
                                {formatMessage({ id: "trainingGoal" })}
                            </span>

                            <span>
                                {formatMessage({ id: "score" })}
                            </span>
                        </h3>

                        {trainingGoals.map(({ id, color, value, name, description, expired }) => <div
                            key={name}
                            css={css`margin-bottom: 0.5em;`}
                        >
                            <div
                                css={[
                                    style.trainingGoalItem,
                                    expired ? appearance.isExpired(showExpired) : null 
                                ]}
                                onClick={() => handleClickItem(id)}
                            >
                                <div css={css`color: ${colors.light}; font-size: 1.25em;`}>
                                    {name}
                                </div>
                                <ScoreIndicator
                                    isNull={value === null || value === undefined}
                                    color={color}
                                    valueString={value + ''}
                                    notApplicableAbbrString={formatMessage({ id: "notApplicableAbbr" })}
                                />
                            </div>

                            {selectedItem === id &&
                                <div
                                    dangerouslySetInnerHTML={{ __html: dp.sanitize(description) }}
                                    css={style.info}
                                />
                            }

                        </div>)}
                    </>
                }
            </>
        }
        {...props}
    />
}

const style = {
    trainingGoalItem: css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5em 1em;
        background: ${colors.eventLight};
        user-select: none;

        cursor: pointer;
        &:hover {
            background: ${lighten(0.05, colors.eventLight)};
        }
    `,
    title: css`
        display: inline-block;
        font-size: 1.2em;
        line-height: 1.2;
        font-family: ${fonts.special};
        font-weight: 700;
        text-transform: uppercase;
    `,
    gauges: css`
        display: flex; 
        justify-content: 
        space-around; 
        padding: 0.5em 1em;
        background: ${colors.eventLight};
        flex-wrap: wrap;
        gap: 0.25em;
        margin-bottom: 1em;
        font-size: 1.2em;
    `,
    info: css`
        padding: 1em;
        background: ${colors.eventDark};
        line-height: 1.2em;
        overflow: hidden;

        * {
            color: ${colors.light} !important;
        }
    `
}

const appearance = {
    h3: css`
        font-size: 1.75em; 
        font-weight: 200; 
        line-height: 1; 
        font-family: ${fonts.special}; 
        color: ${colors.subtle}; 
    `,
    isExpired: (showExpired) =>  css`
        ${!showExpired && 'display: none;'}
        opacity: 0.5; 
        filter: grayscale(1);

        &:hover {
            opacity: 1;
            filter: unset;
        }
    `
}

